import {
  AthleteModifiedPublishedWorkoutDomain,
  Metric,
  WorkoutLayerDomain,
} from '@strength/domain';
import {
  WorkoutLayerCommand,
  WorkoutLayerIdentity,
} from '../block/mutation-fns/types';
import { AthleteHasWorkoutLayer, WorkoutMutationContext } from './types';
import {
  addExerciseMetricEdits as addExerciseMetricEditsModified,
  addSetEdits as addSetEditsModified,
  swapExerciseEdits as swapExerciseEditsModified,
  addExerciseEdits as addExerciseEditsModified,
} from '../block/mutation-fns/workout-modified-published';
import {
  createWorkoutLayerEdit,
  editSetMetricEdits,
  removeExerciseMetricEdits,
  editExerciseEdits,
  removeSetEdits,
  addExerciseMetricEdits as addExerciseMetricEditsDraft,
  addSetEdits as addSetEditsDraft,
  swapExerciseEdits as swapExerciseEditsDraft,
  addExerciseEdits as addExerciseEditsDraft,
  removeSetsFromGroup,
} from '../block/mutation-fns/workout-draft';
import { v4 } from 'uuid';
import {
  HasWorkoutLayer,
  WorkoutSuperset,
  ExerciseMetricMutationData,
  ExerciseMutationData,
  SetFullMutationData,
  SetMetricMutationData,
  Workout,
} from '@strength/domain-workout';
import { SetData } from './tablet/components/create-edit-exercise-sheet/types';

export type ModifiedLayerCallbacks = {
  onSuccess?: () => void;
  onError?: () => void;
};

export type WorkoutLayerEdits = {
  modifiedEdits: WorkoutLayerDomain.WorkoutLayerEdit[];
  draftEdits: WorkoutLayerDomain.WorkoutLayerEdit[];
};

export type ModifiedPublishedWorkoutLayerMutationsFns = {
  executeAllEdits: (
    context: WorkoutMutationContext,
    workoutId: string,
    modifiedEdits: WorkoutLayerDomain.WorkoutLayerEdit[],
    draftEdits: WorkoutLayerDomain.WorkoutLayerEdit[],
    callbacks?: ModifiedLayerCallbacks,
  ) => WorkoutLayerIdentity;
  onEditExercise: (
    context: WorkoutMutationContext,
    workoutId: string,
    workoutExerciseId: string,
    data: Partial<ExerciseMutationData>,
  ) => WorkoutLayerEdits;
  onSwapExercise: (
    context: WorkoutMutationContext,
    workoutId: string,
    workoutSectionId: string,
    previousWorkoutExerciseId: string,
    data: ExerciseMutationData,
    metrics: ExerciseMetricMutationData[],
    sets: SetData[],
    superset?: WorkoutSuperset,
  ) => WorkoutLayerEdits;
  onAddMetric: (
    context: WorkoutMutationContext,
    workoutId: string,
    workoutExerciseId: string,
    data: ExerciseMetricMutationData,
  ) => WorkoutLayerEdits;
  onRemoveMetric: (
    context: WorkoutMutationContext,
    workoutId: string,
    workoutExerciseMetricId: string,
  ) => WorkoutLayerEdits;
  onEditSetMetric: (
    context: WorkoutMutationContext,
    workoutId: string,
    workoutSetId: string,
    data: SetMetricMutationData,
  ) => WorkoutLayerEdits;
  onAddSetMetric: (
    context: WorkoutMutationContext,
    workoutId: string,
    workoutExerciseId: string,
    sortOrder: number,
    metrics: SetMetricMutationData[],
  ) => WorkoutLayerEdits;
  onRemoveSetMetric: (
    context: WorkoutMutationContext,
    workoutId: string,
    workoutSetId: string,
    currentCount: number,
    setIdsToRemove: string[],
  ) => WorkoutLayerEdits;
  onAddExercise: (
    context: WorkoutMutationContext,
    workoutId: string,
    workoutSectionId: string,
    data: ExerciseMutationData,
    metrics: ExerciseMetricMutationData[],
    sets: SetFullMutationData[],
    workoutSupersetId?: string,
    workoutExerciseId?: string,
  ) => WorkoutLayerEdits;
};

export function runAthleteModifiedPublishedMutation(
  context: WorkoutMutationContext,
  commands: WorkoutLayerCommand[],
  bulkMutate: (
    commands: readonly AthleteModifiedPublishedWorkoutDomain.Command[],
  ) => void,
) {
  const athleteModifiedPublishedWorkoutCommands: AthleteModifiedPublishedWorkoutDomain.Command[] =
    [];

  for (const command of commands) {
    // AthleteModifiedPublishedWorkoutDomain.Edit spirtitually is a subclass of WorkoutLayerDomain.HasWorkoutLayerEdit, which makes this a safe cast
    const edits = command.edits as AthleteModifiedPublishedWorkoutDomain.Edit[];
    const { exists, workoutId, id } = command.identity;
    const { blockId, profileId, athleteVersion, blockVersion } = context;

    if (!exists) {
      edits.unshift({
        modelName: 'AthleteModifiedPublishedWorkoutLayer',
        modelId: id,
        action: 'create',
        data: {
          layer: WorkoutLayerDomain.defaultLayer(),
        },
      });
    }

    athleteModifiedPublishedWorkoutCommands.push({
      module: AthleteModifiedPublishedWorkoutDomain.Module,
      identity: {
        workoutId,
        blockId,
        profileId,
        athleteVersion,
        blockVersion,
      },
      edits,
    });
  }

  bulkMutate(athleteModifiedPublishedWorkoutCommands);
}

export function makeModifiedPublishedWorkoutLayerMutationFns(
  modifiedLayers: AthleteHasWorkoutLayer[],
  athleteDraftLayers: AthleteHasWorkoutLayer[],
  blockDraftLayers: HasWorkoutLayer[],
  workout: Workout | undefined,
  setsMetric: Metric,
  mutateFn: (
    context: WorkoutMutationContext,
    command: WorkoutLayerCommand,
    draftCommand: WorkoutLayerCommand,
    callbacks?: ModifiedLayerCallbacks,
  ) => void,
) {
  const getEdits = (
    context: WorkoutMutationContext,
    workoutId: string,
    editGenerator: ({
      identity,
      workoutLayer,
    }: {
      identity: WorkoutLayerIdentity;
      workoutLayer?: HasWorkoutLayer;
    }) => WorkoutLayerDomain.WorkoutLayerEdit[],
  ) => {
    const { identity, workoutLayer } = findWorkoutLayer(
      modifiedLayers.filter((wl) => wl.profileId === context.profileId),
      workoutId,
      'AthleteModifiedPublishedWorkoutLayer',
    );

    const edits = editGenerator({ identity, workoutLayer });
    return edits;
  };

  const executeAllEdits = (
    context: WorkoutMutationContext,
    workoutId: string,
    allModifiedEdits: WorkoutLayerDomain.WorkoutLayerEdit[],
    allDraftEdits: WorkoutLayerDomain.WorkoutLayerEdit[],
    callbacks?: ModifiedLayerCallbacks,
  ) => {
    const { identity: modifiedIdentity, workoutLayer: modifiedLayer } =
      findWorkoutLayer(
        modifiedLayers.filter((wl) => wl.profileId === context.profileId),
        workoutId,
        'AthleteModifiedPublishedWorkoutLayer',
      );

    const { identity: draftIdentity, workoutLayer: draftLayer } =
      findWorkoutLayer(
        athleteDraftLayers.filter((wl) => wl.profileId === context.profileId),
        workoutId,
        'AthleteDraftWorkoutLayer',
      );

    mutateFn(
      context,
      createWorkoutLayerEdit(modifiedIdentity, modifiedLayer, allModifiedEdits),
      createWorkoutLayerEdit(draftIdentity, draftLayer, allDraftEdits),
      callbacks,
    );

    return modifiedIdentity;
  };

  return {
    executeAllEdits: (
      ...params: Parameters<
        ModifiedPublishedWorkoutLayerMutationsFns['executeAllEdits']
      >
    ) => {
      const [context, workoutId, modifiedEdits, draftEdits, callbacks] = params;
      return executeAllEdits(
        context,
        workoutId,
        modifiedEdits,
        draftEdits,
        callbacks,
      );
    },
    onEditExercise: (
      ...params: Parameters<
        ModifiedPublishedWorkoutLayerMutationsFns['onEditExercise']
      >
    ) => {
      const [context, workoutId, workoutExerciseId, data] = params;

      const modifiedEdits = getEdits(context, workoutId, ({ workoutLayer }) =>
        editExerciseEdits(workoutLayer, workoutExerciseId, data),
      );

      const { workoutLayer: draftLayer } = findWorkoutLayer(
        athleteDraftLayers.filter((wl) => wl.profileId === context.profileId),
        workoutId,
        'AthleteDraftWorkoutLayer',
      );

      const draftEdits = editExerciseEdits(draftLayer, workoutExerciseId, data);

      return { modifiedEdits, draftEdits };
    },
    onSwapExercise: (
      ...params: Parameters<
        ModifiedPublishedWorkoutLayerMutationsFns['onSwapExercise']
      >
    ) => {
      const [
        context,
        workoutId,
        workoutSectionId,
        previousWorkoutExerciseId,
        data,
        metrics,
        sets,
        superset,
      ] = params;

      const dataWithId = {
        ...data,
        workoutExerciseId: data.workoutExerciseId ?? v4(),
      };
      const metricsWithIds = metrics.map((m) => ({
        ...m,
        workoutExerciseMetricId: m.workoutExerciseMetricId ?? v4(),
      }));

      const setsWithIds = sets.map((s) => ({
        ...s,
        workoutSetId: v4(),
        workoutSetGroupId: s.workoutSetGroupId || v4(),
      }));

      const supersetExercise = superset?.exercises.find(
        (e) => e.workoutExerciseId === previousWorkoutExerciseId,
      );

      // Modified layer (no Sets metric)
      const modifiedEdits = getEdits(
        context,
        workoutId,
        ({ identity, workoutLayer }) =>
          swapExerciseEditsModified(
            workoutLayer,
            identity?.modelName,
            workoutSectionId,
            previousWorkoutExerciseId,
            dataWithId,
            metricsWithIds,
            setsWithIds,
            supersetExercise,
          ),
      );

      // Draft layer (with Sets metric)
      const { identity: draftIdentity, workoutLayer: draftLayer } =
        findWorkoutLayer(
          athleteDraftLayers.filter((wl) => wl.profileId === context.profileId),
          workoutId,
          'AthleteDraftWorkoutLayer',
        );

      // We need to make sure any grouped sets remain grouped
      const collapsedSets = collapseSets(setsWithIds, setsMetric);

      const draftEdits = swapExerciseEditsDraft(
        draftLayer,
        draftIdentity?.modelName,
        setsMetric,
        workoutSectionId,
        previousWorkoutExerciseId,
        dataWithId,
        metricsWithIds,
        collapsedSets,
        supersetExercise,
        undefined, // alternateGroup
        true, // preserveIds
      );

      return { modifiedEdits, draftEdits };
    },
    onAddMetric: (
      ...params: Parameters<
        ModifiedPublishedWorkoutLayerMutationsFns['onAddMetric']
      >
    ) => {
      const [context, workoutId, workoutExerciseId, data] = params;

      const workoutExerciseMetricId = v4();
      const metricWithId = { ...data, workoutExerciseMetricId };

      const modifiedEdits = getEdits(context, workoutId, () =>
        addExerciseMetricEditsModified(workoutExerciseId, metricWithId),
      );

      const { workoutLayer: draftLayer } = findWorkoutLayer(
        athleteDraftLayers.filter((wl) => wl.profileId === context.profileId),
        workoutId,
        'AthleteDraftWorkoutLayer',
      );

      const baseLayer = blockDraftLayers.find(
        (wl) => wl.workoutId === workoutId,
      );

      const draftEdits = addExerciseMetricEditsDraft(
        draftLayer,
        baseLayer,
        setsMetric,
        workoutExerciseId,
        metricWithId,
        true, // preserveIds
      );

      return { modifiedEdits, draftEdits };
    },
    onRemoveMetric: (
      ...params: Parameters<
        ModifiedPublishedWorkoutLayerMutationsFns['onRemoveMetric']
      >
    ) => {
      const [context, workoutId, workoutExerciseMetricId] = params;

      const modifiedEdits = getEdits(context, workoutId, ({ workoutLayer }) =>
        removeExerciseMetricEdits(workoutLayer, workoutExerciseMetricId),
      );

      const { workoutLayer: draftLayer } = findWorkoutLayer(
        athleteDraftLayers.filter((wl) => wl.profileId === context.profileId),
        workoutId,
        'AthleteDraftWorkoutLayer',
      );

      const draftEdits = removeExerciseMetricEdits(
        draftLayer,
        workoutExerciseMetricId,
      );

      return { modifiedEdits, draftEdits };
    },
    onEditSetMetric: (
      ...params: Parameters<
        ModifiedPublishedWorkoutLayerMutationsFns['onEditSetMetric']
      >
    ) => {
      const [context, workoutId, workoutSetId, data] = params;

      const modifiedEdits = getEdits(context, workoutId, ({ workoutLayer }) =>
        editSetMetricEdits(workoutLayer, workoutSetId, data),
      );

      const { workoutLayer: draftLayer } = findWorkoutLayer(
        athleteDraftLayers.filter((wl) => wl.profileId === context.profileId),
        workoutId,
        'AthleteDraftWorkoutLayer',
      );

      const draftEdits = editSetMetricEdits(draftLayer, workoutSetId, data);

      return { modifiedEdits, draftEdits };
    },
    onAddSetMetric: (
      ...params: Parameters<
        ModifiedPublishedWorkoutLayerMutationsFns['onAddSetMetric']
      >
    ) => {
      const [context, workoutId, workoutExerciseId, sortOrder, metrics] =
        params;

      const workoutSetId = v4();
      const workoutSetGroupId = v4();

      const modifiedEdits = getEdits(context, workoutId, () =>
        addSetEditsModified(
          workoutExerciseId,
          { sortOrder, workoutSetId, workoutSetGroupId },
          metrics,
        ),
      );

      const draftEdits = addSetEditsDraft(
        setsMetric,
        workoutExerciseId,
        { sortOrder, workoutSetId, workoutSetGroupId },
        metrics,
        true, // preserveIds
      );

      return { modifiedEdits, draftEdits };
    },
    onRemoveSetMetric: (
      ...params: Parameters<
        ModifiedPublishedWorkoutLayerMutationsFns['onRemoveSetMetric']
      >
    ) => {
      const [context, workoutId, workoutSetId, currentCount, setIdsToRemove] =
        params;

      const modifiedEdits: WorkoutLayerDomain.WorkoutLayerEdit[] = [];

      // On the modified layer, we are removing individual entries
      setIdsToRemove.forEach((id) => {
        modifiedEdits.push(
          ...getEdits(context, workoutId, ({ workoutLayer }) =>
            removeSetEdits(workoutLayer, id),
          ),
        );
      });

      const { workoutLayer: draftLayer } = findWorkoutLayer(
        athleteDraftLayers.filter((wl) => wl.profileId === context.profileId),
        workoutId,
        'AthleteDraftWorkoutLayer',
      );

      // On the draft layer, we decrement the set count in the group
      const newSetCount = currentCount - setIdsToRemove.length;
      const draftEdits = removeSetsFromGroup(
        draftLayer,
        workoutSetId,
        setsMetric,
        newSetCount < 0 ? 0 : newSetCount,
      );

      return { modifiedEdits, draftEdits };
    },
    onAddExercise: (
      ...params: Parameters<
        ModifiedPublishedWorkoutLayerMutationsFns['onAddExercise']
      >
    ) => {
      const [
        context,
        workoutId,
        workoutSectionId,
        data,
        metrics,
        sets,
        workoutSupersetId,
        providedExerciseId,
      ] = params;

      const workoutExerciseId = providedExerciseId ?? v4();
      const metricsWithIds = metrics.map((m) => ({
        ...m,
        workoutExerciseMetricId: m.workoutExerciseMetricId ?? v4(),
      }));
      const setsWithIds = sets.map((s) => ({
        ...s,
        workoutSetId: s.workoutSetId ?? v4(),
        workoutSetGroupId: s.workoutSetGroupId ?? v4(),
      }));

      const modifiedEdits = getEdits(context, workoutId, () =>
        addExerciseEditsModified(
          workoutSectionId,
          data,
          metricsWithIds,
          setsWithIds,
          undefined,
          workoutSupersetId,
          workoutExerciseId,
        ),
      );

      const draftEdits = addExerciseEditsDraft(
        setsMetric,
        workoutSectionId,
        data,
        metricsWithIds,
        setsWithIds,
        undefined,
        workoutSupersetId,
        workoutExerciseId,
      );

      return { modifiedEdits, draftEdits };
    },
  };
}

function findWorkoutLayer(
  workoutLayers: HasWorkoutLayer[],
  workoutId: string,
  modelName:
    | 'AthleteModifiedPublishedWorkoutLayer'
    | 'AthleteDraftWorkoutLayer',
): {
  workoutLayer: HasWorkoutLayer | undefined;
  identity: WorkoutLayerIdentity;
} {
  const workoutLayer = workoutLayers.find((m) => m.workoutId === workoutId);

  if (!workoutLayer) {
    return {
      workoutLayer,
      identity: {
        workoutId,
        id: v4(),
        exists: false,
        modelName,
      },
    };
  } else {
    return {
      workoutLayer,
      identity: {
        workoutId,
        id: workoutLayer.id,
        exists: true,
        modelName,
      },
    };
  }
}

/**
 * Collapse expanded sets into rows based on matching workoutSetGroupId
 */
export function collapseSets(
  sets: SetFullMutationData[],
  setsMetric: Metric,
): SetFullMutationData[] {
  if (sets.length === 0) return [];

  // Group sets by workoutSetGroupId
  const groups: Map<string, SetFullMutationData[]> = new Map();
  for (const set of sets) {
    const key = set.workoutSetGroupId ?? '';
    const existing = groups.get(key) ?? [];
    existing.push(set);
    groups.set(key, existing);
  }

  // Create collapsed rows - one per group with Sets=count
  const collapsedSets: SetFullMutationData[] = [];

  for (const groupSets of groups.values()) {
    const firstSet = groupSets[0];
    if (!firstSet) continue;
    const count = groupSets.length;

    // Use the minimum sortOrder from the group
    const sortOrder = Math.min(...groupSets.map((s) => s.sortOrder));

    // Use first set's IDs, add Sets metric with count
    const metricsWithSets: SetMetricMutationData[] = [
      { metricId: setsMetric.id, input: String(count), forCollection: false },
      ...firstSet.metrics.filter((m) => m.metricId !== setsMetric.id),
    ];

    collapsedSets.push({
      ...firstSet,
      sortOrder: sortOrder,
      metrics: metricsWithSets,
    });
  }

  return collapsedSets;
}
